import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { defaultTitle, titles } from '@/libs/constant';
import { useSelector } from 'react-redux';
import { loggedUser } from '@/store/features/userSlice';

// Layouts
import DefaultLayout from '@/layout/DefaultLayout';
import GuestLayout from '@/layout/GuestLayout';

// Pages
import Page404 from '@/pages/404';
import DownloadWeAll from '@/pages/download-weall';
import ShareWeAll from '@/pages/share-weall';
import ShareCommunity from '@/pages/share-community';
import ShareGroup from '@/pages/share-group';
import ShareActivity from '@/pages/share-activity';
import ShareReferral from '@/pages/share-referral';
import ShareResetPassword from '@/pages/share-reset-password';
import ShareFreelancer from '@/pages/share-freelancer';
import Dashboard from '@/pages/dashboard';
import SignIn from '@/pages/sign-in';
import ForgotPassword from '@/pages/forgot-password';
import VerifyOtp from '@/pages/verify-otp';
import ResetPassword from '@/pages/reset-password';
import EditProfile from '@/pages/edit-profile';
import ChangePassword from '@/pages/change-password';
import Translation from '@/pages/masters/translation/list';
import AddTranslation from '@/pages/masters/translation/add';
import EditTranslation from '@/pages/masters/translation/[translation_id]';
import CmsList from '@/pages/masters/cms-master/list';
import CmsPublicPage from '@/pages/cms/[slug]';
import AddCms from '@/pages/masters/cms-master/add';
import EditCms from '@/pages/masters/cms-master/[cms_id]';
import EmailTemplateList from '@/pages/masters/email-templates/list';
import AddEmailTemplate from '@/pages/masters/email-templates/add';
import EditEmailTemplate from '@/pages/masters/email-templates/[email_template_id]';
import SmsTemplateList from '@/pages/masters/sms-templates/list';
import AddSmsTemplate from '@/pages/masters/sms-templates/add';
import EditSmsTemplate from '@/pages/masters/sms-templates/[sms_template_id]';
import ReportedUsersList from '@/pages/masters/report-reasons/list';
import reportedUsersTranslations from '@/constants/translation/reportedUsers.translation';
import ServiceCategoryList from '@/pages/masters/service-categories/list';
import AddServiceCategory from '@/pages/masters/service-categories/add';
import EditServiceCategory from '@/pages/masters/service-categories/[service_category_id]';
import SubscriptionPlanList from '@/pages/masters/subscription-plans/list';
import AddSubscriptionPlan from '@/pages/masters/subscription-plans/add';
import EditSubscriptionPlan from '@/pages/masters/subscription-plans/[subscription_plan_id]';
import SubscribedUserList from '@/pages/masters/subscribed-users/list';
import ViewSubscribedUser from '@/pages/masters/subscribed-users/[subscribed_user_id]/view';
import ReferralRecordList from '@/pages/masters/referral-records/list';
import ViewReferralRecord from '@/pages/masters/referral-records/[referral_record_id]/view';
import CommunityLeaderboardList from '@/pages/masters/community-leaderboard/list';
import GeneralSettings from '@/pages/settings/general-settings';
import ReferralSettings from '@/pages/settings/referral-settings';
import AdminRewardsSettings from '@/pages/settings/admin-rewards-settings';
import UserList from '@/pages/user/list';
import ViewUser from '@/pages/user/[user_id]';
import DummyUserList from '@/pages/user/dummy/list';
import AddDummyUser from '@/pages/user/dummy/add';
import EditDummyUser from '@/pages/user/dummy/[dummy_user_id]/edit';
import ViewDummyUser from '@/pages/user/dummy/[dummy_user_id]/view';
import FreelancerUserList from '@/pages/user/freelancer/list';
import ViewFreelancerUser from '@/pages/user/freelancer/[freelancer_user_id]/view';
import ViewFreelancerService from '@/pages/user/freelancer/[freelancer_user_id]/service/[service_id]/view';
import ReviewList from '@/pages/reviews/list';
import ViewReview from '@/pages/reviews/[review_id]/view';
import NotificationList from '@/pages/notification/list';
import FaviconUpdater from '@/components/FaviconUpdater';

// Route protection wrapper

// Page title manager
const PageTitle = ({ title }) => {
  useEffect(() => {
    document.title = title || defaultTitle;
  }, [title]);
  return null;
};

/**
 * @name App
 * @description Main App component with React Router setup
 */
const App = () => {
  const [favicon, setFavicon] = useState('/favicon.ico');

  useEffect(() => {
    // Fetch favicon URL from the API

    //fetchFavicon();
  }, []);

  return (
    <Router>
      <FaviconUpdater />
      <Routes>
        {/* Guest Routes - No layout or GuestLayout */}
        <Route path="/" element={<HomeWrapper />} />
        <Route path="/sign-in" element={<SignInWrapper />} />
        <Route path="/forgot-password" element={<ForgotPasswordWrapper />} />
        <Route path="/verify-otp" element={<VerifyOtpWrapper />} />
        <Route path="/reset-password" element={<ResetPasswordWrapper />} />
        <Route path="/download-weall" element={<DownloadWeAll />} />
        <Route path="/share-weall" element={<ShareWeAll />} />
        <Route path="/share-community" element={<ShareCommunity />} />
        <Route path="/share-group" element={<ShareGroup />} />
        <Route path="/share-activity" element={<ShareActivity />} />
        <Route path="/share-referral" element={<ShareReferral />} />
        <Route path="/share-reset-password" element={<ShareResetPassword />} />
        <Route path="/share-freelancer" element={<ShareFreelancer />} />
        {/* Public CMS pages (no login): content from Admin > CMS Master, opened in the app's web view */}
        <Route path="/cms/:slug" element={<CmsPublicPage />} />

        {/* Protected Routes - DefaultLayout */}
        <Route path="/dashboard" element={
          <ProtectedRouteWrapper breadCrumbArray={Dashboard.breadCrumbArray}>
            <PageTitle title="Dashboard - WeAll" />
            <Dashboard />
          </ProtectedRouteWrapper>
        } />

        <Route path="/user/list" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'User List', pathname: '/user/list', active: true }
          ]}>
            <PageTitle title={titles.userMaster.list} />
            <UserList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/user/:id/view" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'User List', pathname: '/user/list', active: false },
            { name: 'User View', pathname: '', active: true }
          ]}>
            <PageTitle title={titles.userMaster.view} />
            <ViewUser />
          </ProtectedRouteWrapper>
        } />

        <Route path="/user/dummy/list" element={
          <ProtectedRouteWrapper breadCrumbArray={DummyUserList.breadCrumbArray}>
            <PageTitle title={titles.dummyUser.list} />
            <DummyUserList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/user/dummy/add" element={
          <ProtectedRouteWrapper breadCrumbArray={AddDummyUser.breadCrumbArray}>
            <PageTitle title={titles.dummyUser.add} />
            <AddDummyUser />
          </ProtectedRouteWrapper>
        } />

        <Route path="/user/dummy/:id/edit" element={
          <ProtectedRouteWrapper breadCrumbArray={EditDummyUser.breadCrumbArray}>
            <PageTitle title={titles.dummyUser.edit} />
            <EditDummyUser />
          </ProtectedRouteWrapper>
        } />

        <Route path="/user/dummy/:id/view" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'Dummy User List', pathname: '/user/dummy/list', active: false },
            { name: 'Dummy User View', pathname: '', active: true }
          ]}>
            <PageTitle title={titles.dummyUser.view} />
            <ViewDummyUser />
          </ProtectedRouteWrapper>
        } />

        <Route path="/user/freelancer/list" element={
          <ProtectedRouteWrapper breadCrumbArray={FreelancerUserList.breadCrumbArray}>
            <PageTitle title={titles.freelancerUser.list} />
            <FreelancerUserList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/user/freelancer/:id/view" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'Freelancer User List', pathname: '/user/freelancer/list', active: false },
            { name: 'Freelancer User View', pathname: '', active: true }
          ]}>
            <PageTitle title={titles.freelancerUser.view} />
            <ViewFreelancerUser />
          </ProtectedRouteWrapper>
        } />

        <Route path="/user/freelancer/:id/service/:serviceId/view" element={<FreelancerServiceViewWrapper />} />

        <Route path="/reviews/list" element={
          <ProtectedRouteWrapper breadCrumbArray={ReviewList.breadCrumbArray}>
            <PageTitle title={titles.review.list} />
            <ReviewList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/reviews/:id/view" element={
          <ProtectedRouteWrapper breadCrumbArray={ViewReview.breadCrumbArray}>
            <PageTitle title={titles.review.view} />
            <ViewReview />
          </ProtectedRouteWrapper>
        } />

        <Route path="/notification-list" element={
          <ProtectedRouteWrapper breadCrumbArray={NotificationList.breadCrumbArray}>
            <PageTitle title={titles.notification.list} />
            <NotificationList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/edit-profile" element={
          <ProtectedRouteWrapper breadCrumbArray={[]}>
            <PageTitle title="Edit Profile - WeAll" />
            <EditProfile />
          </ProtectedRouteWrapper>
        } />

        <Route path="/change-password" element={
          <ProtectedRouteWrapper breadCrumbArray={[]}>
            <PageTitle title="Change Password - WeAll" />
            <ChangePassword />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/translation/list" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'Translation List', pathname: '/masters/translation/list', active: true }
          ]}>
            <PageTitle title="Translation List - WeAll" />
            <Translation />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/translation/add" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'Translation List', pathname: '/masters/translation/list', active: false },
            { name: 'Add Translation', pathname: '/masters/translation/add', active: true }
          ]}>
            <PageTitle title="Add Translation - WeAll" />
            <AddTranslation />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/translation/:id" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'Translation List', pathname: '/masters/translation/list', active: false },
            { name: 'Edit Translation', pathname: '', active: true }
          ]}>
            <PageTitle title="Edit Translation - WeAll" />
            <EditTranslation />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/cms/list" element={
          <ProtectedRouteWrapper breadCrumbArray={CmsList.breadCrumbArray}>
            <PageTitle title="CMS Master - WeAll" />
            <CmsList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/cms/add" element={
          <ProtectedRouteWrapper breadCrumbArray={AddCms.breadCrumbArray}>
            <PageTitle title="Add CMS Page - WeAll" />
            <AddCms />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/cms/:id" element={
          <ProtectedRouteWrapper breadCrumbArray={EditCms.breadCrumbArray}>
            <PageTitle title="Edit CMS Page - WeAll" />
            <EditCms />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/email-templates/list" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'Email Template List', pathname: '/masters/email-templates/list', active: true }
          ]}>
            <PageTitle title="Email Template List - WeAll" />
            <EmailTemplateList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/email-templates/add" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'Email Template List', pathname: '/masters/email-templates/list', active: false },
            { name: 'Add Email Template', pathname: '/masters/email-templates/add', active: true }
          ]}>
            <PageTitle title="Add Email Template - WeAll" />
            <AddEmailTemplate />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/email-templates/:id" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'Email Template List', pathname: '/masters/email-templates/list', active: false },
            { name: 'Edit Email Template', pathname: '', active: true }
          ]}>
            <PageTitle title="Edit Email Template - WeAll" />
            <EditEmailTemplate />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/sms-templates/list" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'SMS Template List', pathname: '/masters/sms-templates/list', active: true }
          ]}>
            <PageTitle title="SMS Template List - WeAll" />
            <SmsTemplateList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/sms-templates/add" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'SMS Template List', pathname: '/masters/sms-templates/list', active: false },
            { name: 'Add SMS Template', pathname: '/masters/sms-templates/add', active: true }
          ]}>
            <PageTitle title="Add SMS Template - WeAll" />
            <AddSmsTemplate />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/sms-templates/:id" element={
          <ProtectedRouteWrapper breadCrumbArray={[
            { name: 'SMS Template List', pathname: '/masters/sms-templates/list', active: false },
            { name: 'Edit SMS Template', pathname: '', active: true }
          ]}>
            <PageTitle title="Edit SMS Template - WeAll" />
            <EditSmsTemplate />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/reported-users/list" element={<ReportedUsersRoute />} />

        <Route path="/masters/report-reasons/list" element={
          <Navigate to="/masters/reported-users/list" replace />
        } />
        <Route path="/masters/report-reasons/*" element={
          <Navigate to="/masters/reported-users/list" replace />
        } />

        <Route path="/masters/service-categories/list" element={
          <ProtectedRouteWrapper breadCrumbArray={ServiceCategoryList.breadCrumbArray}>
            <PageTitle title="Service Category List - WeAll" />
            <ServiceCategoryList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/service-categories/add" element={
          <ProtectedRouteWrapper breadCrumbArray={AddServiceCategory.breadCrumbArray}>
            <PageTitle title="Add Service Category - WeAll" />
            <AddServiceCategory />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/service-categories/:id" element={
          <ProtectedRouteWrapper breadCrumbArray={EditServiceCategory.breadCrumbArray}>
            <PageTitle title="Edit Service Category - WeAll" />
            <EditServiceCategory />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/subscription-plans/list" element={
          <ProtectedRouteWrapper breadCrumbArray={SubscriptionPlanList.breadCrumbArray}>
            <PageTitle title="Subscription Plan List - WeAll" />
            <SubscriptionPlanList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/subscription-plans/add" element={
          <ProtectedRouteWrapper breadCrumbArray={AddSubscriptionPlan.breadCrumbArray}>
            <PageTitle title="Add Subscription Plan - WeAll" />
            <AddSubscriptionPlan />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/subscription-plans/:id" element={
          <ProtectedRouteWrapper breadCrumbArray={EditSubscriptionPlan.breadCrumbArray}>
            <PageTitle title="Plan View - WeAll" />
            <EditSubscriptionPlan />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/subscribed-users/list" element={
          <ProtectedRouteWrapper breadCrumbArray={SubscribedUserList.breadCrumbArray}>
            <PageTitle title="Subscribed Users - WeAll" />
            <SubscribedUserList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/subscribed-users/:id/view" element={
          <ProtectedRouteWrapper breadCrumbArray={ViewSubscribedUser.breadCrumbArray}>
            <PageTitle title="Subscribed User View - WeAll" />
            <ViewSubscribedUser />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/referral-records/list" element={
          <ProtectedRouteWrapper breadCrumbArray={ReferralRecordList.breadCrumbArray}>
            <PageTitle title="Referral Records - WeAll" />
            <ReferralRecordList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/referral-records/:id/view" element={
          <ProtectedRouteWrapper breadCrumbArray={ViewReferralRecord.breadCrumbArray}>
            <PageTitle title="Referral Record View - WeAll" />
            <ViewReferralRecord />
          </ProtectedRouteWrapper>
        } />

        <Route path="/masters/community-leaderboard/list" element={
          <ProtectedRouteWrapper breadCrumbArray={CommunityLeaderboardList.breadCrumbArray}>
            <PageTitle title="Community Leaderboard - WeAll" />
            <CommunityLeaderboardList />
          </ProtectedRouteWrapper>
        } />

        <Route path="/settings/general-settings" element={
          <ProtectedRouteWrapper breadCrumbArray={[]}>
            <PageTitle title="General Settings - WeAll" />
            <GeneralSettings />
          </ProtectedRouteWrapper>
        } />

        <Route path="/settings/referral-settings" element={
          <ProtectedRouteWrapper breadCrumbArray={[]}>
            <PageTitle title="Referral & Rewards Settings - WeAll" />
            <ReferralSettings />
          </ProtectedRouteWrapper>
        } />

        <Route path="/settings/admin-rewards-settings" element={
          <ProtectedRouteWrapper breadCrumbArray={[]}>
            <PageTitle title="Admin Rewards Settings - WeAll" />
            <AdminRewardsSettings />
          </ProtectedRouteWrapper>
        } />

        {/* 404 Route */}
        <Route path="*" element={<Page404 />} />
      </Routes>
    </Router>
  );
};

// Wrapper components for layouts
const HomeWrapper = () => {

  useEffect(() => {
    // Redirect to sign-in
    window.location.href = '/sign-in';
  }, []);

  return null;
};

const SignInWrapper = () => (
  <GuestLayout>
    <PageTitle title="Sign In - WeAll" />
    <SignIn />
  </GuestLayout>
);

const ForgotPasswordWrapper = () => (
  <GuestLayout>
    <PageTitle title="Forgot Password - WeAll" />
    <ForgotPassword />
  </GuestLayout>
);

const VerifyOtpWrapper = () => (
  <GuestLayout>
    <PageTitle title="Verify OTP - WeAll" />
    <VerifyOtp />
  </GuestLayout>
);

const ResetPasswordWrapper = () => (
  <GuestLayout>
    <PageTitle title="Reset Password - WeAll" />
    <ResetPassword />
  </GuestLayout>
);

const FreelancerServiceViewWrapper = () => {
  const { id } = useParams();

  return (
    <ProtectedRouteWrapper breadCrumbArray={[
      { name: 'Freelancer User List', pathname: '/user/freelancer/list', active: false },
      { name: 'View Profile', pathname: `/user/freelancer/${id}/view`, active: false },
      { name: 'Service Details', pathname: '', active: true }
    ]}>
      <PageTitle title={titles.freelancerUser.serviceView} />
      <ViewFreelancerService />
    </ProtectedRouteWrapper>
  );
};

const ProtectedRouteWrapper = ({ children, breadCrumbArray }) => {
  const checkLoggenInUser = useSelector(loggedUser);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Add a small delay to ensure Redux persist is fully rehydrated
    const timer = setTimeout(() => {
      setIsAuthenticated(checkLoggenInUser?.isLoggedIn);
      setIsChecking(false);
    }, 50);

    return () => clearTimeout(timer);
  }, [checkLoggenInUser]);

  if (isChecking || isAuthenticated === null) {
    return <div className="app-loading"><div className="spinner"></div></div>;
  }

  if (!isAuthenticated) {
    window.location.href = '/sign-in';
    return null;
  }

  return (
    <DefaultLayout breadCrumbArray={breadCrumbArray}>
      {children}
    </DefaultLayout>
  );
};

/**
 * Reported Users/Feed share one page behind two tabs (see ReportedUsersList) rather than two
 * routes, so the breadcrumb - unlike every other page's static breadCrumbArray - has to be
 * resolved per-render from the `tab` query param instead of being fixed at route-registration
 * time. Kept local to this one route rather than changing ProtectedRouteWrapper/DefaultLayout/
 * AppHeader, which every other (static-breadcrumb) page also relies on.
 */
const ReportedUsersRoute = () => {
  const [searchParams] = useSearchParams();
  const isFeedTab = searchParams.get('tab') === 'feed';
  const reportedUsersText = reportedUsersTranslations.list;
  const breadCrumbArray = isFeedTab
    ? [
        {
          name: reportedUsersText.tabs.feed,
          pathname: '/masters/reported-users/list',
          active: true,
        },
      ]
    : ReportedUsersList.breadCrumbArray;

  return (
    <ProtectedRouteWrapper breadCrumbArray={breadCrumbArray}>
      <PageTitle title="Reported Users - WeAll" />
      <ReportedUsersList />
    </ProtectedRouteWrapper>
  );
};

export default App;
